import { UserFacingError } from '@features/shared/api/client'
import { useAuth } from '@clerk/clerk-react'
import { useIsMutating, useQuery, useQueryClient } from '@tanstack/react-query'
import type { MicrosoftConnectionStatus } from '../api/contracts'
import { getMicrosoftSenders } from '../api/integrations.api'
import { integrationsQueryKeys } from '../api/queryKeys'
import { useMicrosoftConnectionStatus } from './useMicrosoftConnectionStatus'

const SENDERS_STALE_TIME = 5 * 60_000

/**
 * Session-only picker data: the senders a person may choose, which is what
 * an email step's Apply relies on (the server checks again at publish).
 * Read by the email step's sender picker while it's open, and only then: each
 * read scans every mailbox in Exchange Online, so nothing warms or polls it.
 */
export function useMicrosoftSenderDirectory() {
  const { userId, orgId, sessionId, isLoaded } = useAuth()
  const client = useQueryClient()
  const connectionKey = integrationsQueryKeys.microsoftConnection(userId, orgId)
  const connectionQuery = useMicrosoftConnectionStatus()
  const connection = connectionQuery.data
  const changing = useIsMutating({ mutationKey: [...connectionKey, 'change'] }) > 0
  const enabled =
    isLoaded &&
    !!userId &&
    !!orgId &&
    !changing &&
    connection?.configured === true &&
    connection.status === 'connected' &&
    !!connection.connection_id
  const query = useQuery({
    queryKey: [
      ...connectionKey,
      'senders',
      sessionId,
      connection?.connection_id,
      connection?.microsoft_tenant_id,
      connection?.connected_at,
      connection?.status,
    ],
    queryFn: async ({ signal }) => {
      const result = await getMicrosoftSenders(signal)
      if (result.connection_id !== connection?.connection_id) {
        throw Object.assign(
          new UserFacingError('The Microsoft connection changed. Sender choices are updating.'),
          { status: 409 }
        )
      }
      return result
    },
    enabled,
    staleTime: SENDERS_STALE_TIME,
    gcTime: SENDERS_STALE_TIME,
    // Only the open picker reads it, so this refreshes the choices on screen.
    refetchOnWindowFocus: true,
    retry: (count, error) =>
      count < 1 && ![401, 403, 409].includes((error as Error & { status?: number }).status ?? 0),
    retryDelay: 3_000,
  })
  // A transient refresh failure does not remove previously verified choices.
  // Explicit authority failures do, and a different connection/session uses a
  // different cache entry. None of these observations substitutes for Apply.
  const blocked = [query.error, connectionQuery.error].some((error) =>
    [401, 403, 409].includes((error as (Error & { status?: number }) | null)?.status ?? 0)
  )
  const available = enabled && !blocked && !!query.data
  return {
    query,
    enabled,
    connectionQuery,
    senders: available ? query.data!.senders : [],
    hasData: available,
    canUseIdentity:
      isLoaded &&
      !!userId &&
      !!orgId &&
      !changing &&
      !blocked &&
      !query.data &&
      (enabled || (!connection && !connectionQuery.isError)),
    loading: !available && (connectionQuery.isLoading || (enabled && query.isFetching)),
    isCurrent: (expected = connection) => {
      const current = client.getQueryData<MicrosoftConnectionStatus>(connectionKey)
      return (
        current?.configured === true &&
        current.status === 'connected' &&
        !!current.connection_id &&
        current.connection_id === expected?.connection_id &&
        current.connected_at === expected?.connected_at &&
        current.microsoft_tenant_id === expected?.microsoft_tenant_id &&
        client.isMutating({ mutationKey: [...connectionKey, 'change'] }) === 0
      )
    },
  }
}
